import type { FC, ReactElement } from 'react';
import { memo } from 'react';

import produce from 'immer'

import { ChecklistPopoverStyled } from './styles/ChecklistPopoverStyled'
import { MenuList, MenuListItem } from '../../blocks/MenuList'
import { TransparentIconButton } from '@ui/elements/Button'
import { Checkbox } from '@ui/elements/Checkbox'
import { Icon } from '@ui/elements/Icon'

export interface ChecklistItem {
  label: string
  value: any
  checked?: boolean
  locked?: boolean
}

export interface IChecklistPopoverProps {
  checklist: ChecklistItem[]
  /**
   * Unique identifier for analytics tracking of checklist popover interactions.
   */
  analyticsId?: string
  onUpdate?: (updated: ChecklistItem[], nextState: ChecklistItem[]) => void
}

const getItemIcon = (item: ChecklistItem): ReactElement => {
  if (item.locked) {
    return <Icon icon='lock' />
  } else if (item.checked) {
    return <Checkbox inputProps={{ title: item.label }} checked={true} />
  } else {
    return <Checkbox inputProps={{ title: item.label }} checked={false} />
  }
}

export const ChecklistPopover: FC<IChecklistPopoverProps> = memo(
  ({ checklist: checklistProp, onUpdate, analyticsId }) => {
    const onItemSelect = (item, itemIndex) => {
      if (item.locked) return

      const nextState = produce(checklistProp, (draft) => {
        draft[itemIndex].checked = !draft[itemIndex].checked
      })

      onUpdate([nextState[itemIndex]], nextState)
    }

    const toggleSelectAll = (checked) => {
      const nextState = produce(checklistProp, (draft) => {
        draft.forEach((item) => {
          if (item.locked) return

          item.checked = !!checked
        })
      })

      onUpdate(nextState, nextState)
    }

    return (
      <ChecklistPopoverStyled>
        <MenuList
          triggerElement={
            <TransparentIconButton
              analyticsId={analyticsId}
              type='button'
              icon='settings'
              aria-label='Open options menu'
            />
          }
        >
          <>
            {checklistProp.length > 0 &&
              checklistProp.map((item, itemIndex) => (
                <MenuListItem
                  onClick={() => onItemSelect(item, itemIndex)}
                  icon={getItemIcon(item)}
                  key={itemIndex}
                  text={item.label}
                />
              ))}
          </>
          <MenuListItem
            sticky
            onClick={() => toggleSelectAll(true)}
            text='Select All'
          />
          <MenuListItem
            sticky
            onClick={() => toggleSelectAll(false)}
            text='Deselect All'
          />
        </MenuList>
      </ChecklistPopoverStyled>
    )
  }
)

ChecklistPopover.defaultProps = {
  checklist: [],
  onUpdate: () => undefined,
}
ChecklistPopover.displayName = 'ChecklistPopover'
